import React, { createContext, useCallback, useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { EuiSpacer } from "@elastic/eui";
import _ from "lodash";
import { extractResourceAttributes } from "../../../../../utils/jsonApiUtils";
import { entitySelector, fetchEntity, updateEntity } from "../../slice";

import ProfileDetails from "./ProfileDetails";
import ProfileEntities from "./ProfileEntities";

import Api from "../../../../../lib/api";
import { singular } from "../../../../../utils/stringUtils";

export const EntityProfileContext = createContext({});

const updateProfileEntity = async ({ entityName, entityId, attributes }) => {
    await Api.put(`/${_.kebabCase(entityName)}/${entityId}`, {
        data: {
            id: +entityId,
            type: entityName,
            attributes,
        },
    });
};

const deleteAttachment = async (attachmentId) => {
    const response = await Api.delete(`attachments/${attachmentId}`);
    return response;
};

const addAttachment = async ({ entityName, entityId, attributes }) => {
    const updateAttachmentsPromise = attributes.attachment.map(async (document) => {
        const body = {
            data: {
                attributes: {
                    document,
                    attachedItemId: +entityId,
                    attachedItemType: _.capitalize(singular(entityName)),
                },
                type: "attachments",
            },
        };

        const response = await Api.post("attachments", body);
        return response;
    });

    return await Promise.all(updateAttachmentsPromise);
};

const handleUpdate = async ({
    isProfileEntity,
    entity: { entityName, entityId, attributes },
    callback,
    dispatch,
    fetch,
}) => {
    if (_.isNil(entityName) || _.isNil(entityId)) {
        callback();
        return;
    }

    try {
        isProfileEntity
            ? await updateProfileEntity({ entityName, entityId, attributes })
            : attributes.attachment
            ? attributes.attachment.id
                ? await deleteAttachment(attributes.attachment.id)
                : await addAttachment({ entityName, entityId, attributes })
            : await dispatch(updateEntity(entityName, entityId, attributes));

        await fetch();
        callback();
    } catch (error) {
        console.log(error);
        callback();
    }
};

function EntityProfile({ options: { entityName, displayName, profileFields, statsFields } }) {
    const { id } = useParams();
    const { data, included } = useSelector(entitySelector(entityName));
    const dispatch = useDispatch();

    const includedFetchString = useMemo(
        () =>
            _.uniq(
                profileFields
                    .filter((field) => field.includeEntityName)
                    .map((field) => field.includeEntityName || field.entityName)
            ).join(","),
        [profileFields]
    );

    const relationFields = useMemo(
        () => profileFields.filter((field) => field.type === "relation"),
        [profileFields]
    );

    const fetch = useCallback(
        () => dispatch(fetchEntity(entityName, id, includedFetchString)),
        [entityName, id, includedFetchString, dispatch]
    );

    useEffect(() => {
        fetch();
    }, [fetch]);

    return (
        <EntityProfileContext.Provider
            value={{
                entityResource: data,
                entity: extractResourceAttributes(data),
                includedResource: included,
                entityName,
                entityDisplayName: displayName || entityName,
                entityId: id,
                fields: profileFields,
                statsFields,
                relationFields,
                fetch,
                onSubmit: (attributes, callback) =>
                    handleUpdate({
                        entity: { entityName, entityId: id, attributes },
                        callback,
                        dispatch,
                        fetch,
                    }),
                onEntityUpdate: ({ entityName, entityId, attributes }, callback) =>
                    handleUpdate({
                        isProfileEntity: true,
                        entity: { entityName, entityId, attributes },
                        callback,
                        dispatch,
                        fetch,
                    }),
            }}
        >
            <EuiSpacer size="l" />
            <ProfileDetails />
            <EuiSpacer size="l" />
            <ProfileEntities organizationId={id} />
        </EntityProfileContext.Provider>
    );
}

export default EntityProfile;
